import { BehaviorSubject, Subject, debounceTime } from 'rxjs';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { Databin } from './databin';
import { XItem } from './x-item';
import { Temporal } from '@js-temporal/polyfill';
import { ParserFile } from './parser-file';
import { ScriptResource } from './script-resource';

export type CTG =
    | 'title'
    | 'hits'
    | 'query'
    | 'finalSearchString'
    | 'isOn'
    | 'position'
    | '_databinId'
    | '_connectorTag'
    | 'description'
    | '_ts'
    | '_hits'
    | '_searchTerm'
    | '_fetchStart'
    | '_searchParams'
    | 'sourceURL'
    | 'isFetchComplete';

export type CPAR = {
    id: string;
    isOn?: boolean;
    databin?: Databin;
    connector?: string;
    xitemIdLs?: string[];
    values: {
        title: string;
        timestamp: string;
        query: string;
        hits: string;
    };
};
export class Connection {
    private _messageWidget?: any;

    activeFetchNumber = 0;
    isFetchError = false;
    isSearchInProgress = false;
    lastFetchErrorText = '';

    private _parserFileLs: ParserFile[] = [];
    private _errorParserFileLs: ParserFile[] = [];

    previewLs: XItem[] = [];

    id!: string;

    private _xitemIdSet: Set<string> = new Set();
    xitemLsChange$ = new Subject<boolean>();
    message$ = new BehaviorSubject<string>('');

    private _valueMap = new Map<CTG, string | boolean | number>();

    private _staleConnectionThresholdMs = 12 * 60 * 60 * 1000; // 12 hours

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(
        private coords: CoordinatorService,
        params: any,
    ) {
        this._valueMap.set('isOn', true);
        this._valueMap.set('_ts', Temporal.Now.instant().epochMilliseconds);

        Object.keys(params).forEach((key: string) => {
            const v = params[key];
            switch (key) {
                case 'id':
                    this.id = v;
                    return;
                case 'isOn':
                    this._valueMap.set('isOn', v !== false);
                    return;
                case 'connector':
                    this._valueMap.set('_connectorTag', v);
                    return;
                case 'xitemIdLs':
                    this._xitemIdSet = new Set(params.xitemIdLs);
                    return;
                default:
                    this._valueMap.set(key as CTG, v);
            }
        });

        if (!this.id) this.id = this.coords.uniqueId();
        if (!this.get('_databinId')) this.set('_databinId', this.coords.databin$.current!.id);

        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    registerParserFile(parserFile: ParserFile) {
        this._parserFileLs.push(parserFile);
    }

    parserFileResult() {
        let suggestNameLs: string[] = [];

        if (this._parserFileLs.every((x) => x.isFinal)) {
            this.previewLs = [];

            const s = new Set<string>();
            this._parserFileLs.forEach((pf) => {
                if (pf.errorMessage) {
                    this._errorParserFileLs.push(pf);
                    return;
                }
                if (pf.xitemLs && pf.xitemLs.length) {
                    pf.xitemLs.forEach((x) => {
                        if (s.has(x.id)) return;
                        s.add(x.id);
                        this.previewLs.push(x);
                    });
                    suggestNameLs.push(pf.suggestName);
                }
            });

            if (this.activeFetchNumber) {
                this.activeFetchNumber = 0;
                this.xitemLsExtend(this.previewLs);
                this.previewLs = [];
                this.isOn = true; // force refresh
            } else {
                if (this.previewLs.length) {
                    this.coords.databin$.communicatorStage$.next(2);
                    const testN = 10;
                    if (suggestNameLs.length > testN) {
                        suggestNameLs = suggestNameLs.slice(0, testN);
                        suggestNameLs.push('…');
                    }

                    this.set('title', suggestNameLs.join(', '));
                } else if (this._errorParserFileLs.length) {
                    this._messageWidget?.refresh('error', 'msg_errorInInput');
                } else {
                    const warningPf = this._parserFileLs.find((pf) => pf.warningMessage);
                    const warningMsg = warningPf?.warningMessage || 'msg_noHits';
                    this._messageWidget?.refresh('warning', warningMsg);
                }
            }
        }
    }

    clear() {
        this._parserFileLs = [];
        this.previewLs = [];
        this.isFetchError = false;
        this.lastFetchErrorText = '';
        this.activeFetchNumber = 0;
    }

    get hits() {
        return this.get('_hits') || this.previewLs.length;
    }

    initMessageWidget(messageWidget: any) {
        this._messageWidget = messageWidget;
        let txt = '';

        switch (this._messageWidget.tag) {
            case 'step2':
                if (this.communicator?.tag === 'files') {
                    const resultsPerFile = this._parserFileLs.map((x) => {
                        return {
                            name: x.suggestName,
                            hits: x.xitemLs?.length,
                            warning: x.warningMessage,
                        };
                    });

                    const resultsPerFileStr = resultsPerFile.map((x, index) => {
                        if (x.hits) {
                            return `${index + 1}) '${x.name}': ${x.hits} ${this.coords.text('resultsFound')}`;
                        }
                        // Use specific warning message if available, otherwise generic "no hits"
                        const msg = x.warning
                            ? this.coords.text(x.warning)
                            : this.coords.text('msg_noHits');
                        return `<span style="color: #e58000">${index + 1}) '${x.name}': ${msg}</span>`;
                    });

                    txt = [`<b>${this.hits} ${this.coords.text('resultsFound')}:</b><br>`]
                        .concat(resultsPerFileStr)
                        .join('<br>');
                } else {
                    txt = `${this.hits} ${this.coords.text('resultsFound')}`;
                }

                break;
        }

        this.refreshMessageWidget('info', txt);
    }

    destroyMessageWidget() {
        this._messageWidget = undefined;
    }

    refreshMessageWidget(type: string, message: string) {
        this._messageWidget?.refresh(type, message);
    }

    get snapshot() {
        return this.previewLs.slice(0, 30);
    }

    hasId(itmId: string) {
        return this._xitemIdSet.has(itmId);
    }

    get connector() {
        return this.coords.databin$.getConnector(this._valueMap.get('_connectorTag') as string)!;
    }

    private _store(params: any) {
        (Object.keys(params) as CTG[]).forEach((k) => {
            this.set(k, params[k]);
        });
    }

    command(params: any) {
        switch (params.name) {
            case 'search-start':
                this.isSearchInProgress = true;
                this.refreshMessageWidget('loading', this.coords.text('msg_searchStarted'));
                break;
            case 'search-end':
                this.isSearchInProgress = false;
                this._store(params.payload);
                break;
            case 'fetch-start':
                this.isFetchError = false;
                const cs$ = this.coords.databin$.communicatorStage$;
                this.refreshMessageWidget('info', this.coords.text('msg_FetchInProgress'));
                if (cs$.getValue() === 4) cs$.next(0);
                break;
            case 'fetch-end':
                break;
            case 'fetch-error':
                this.isFetchError = true;
                this.lastFetchErrorText = params.payload.text;
                this.activeFetchNumber = 0;

                console.log('FETCH ERROR', params);
                break;
            case 'store':
                this._store(params.payload);
                break;
        }
    }

    private _manifestStore() {
        if (this.inProject) {
            this.coords.manifest$.store(this.manifestParams);
            this._manifestIdsStore();
        }
    }

    manifestDelete() {
        this.coords.manifest$.delete([
            {
                id: this.id,
                type: 'connection',
            },
            {
                id: this.id,
                type: 'connection-xitemIdLs',
            },
        ]);
    }

    json() {
        return {
            id: this.id,
            type: this.connector.tag,
            isOn: this.isOn,
            title: this.title,
            items: Array.from(this.xitemIdLs || []),
            timestamp: Temporal.Instant.fromEpochMilliseconds(this.timestamp).toString(),
        };
    }

    private _manifestIdsStore() {
        if (!this._xitemIdSet.size) return;
        this.inProject &&
            this.coords.manifest$.store({
                type: 'connection-xitemIdLs',
                id: this.id,
                valueMap: new Map<string, string[]>([['xitemIdLs', Array.from(this._xitemIdSet)]]),
            });
    }

    get databin() {
        return this.coords.databin$.get(this._valueMap.get('_databinId') as string);
    }

    get manifestParams() {
        const mp = new Map(this._valueMap);
        mp.delete('_searchParams');

        return {
            type: 'connection',
            id: this.id,
            valueMap: mp,
        };
    }

    overlapData() {
        const s = new Set<string>();
        this.xitemIdLs.forEach((xitmId) => {
            const finalId = this.coords.dataset$.preview.getMainIdfromAnyId(xitmId);
            finalId && s.add(finalId);
        });
        return {
            id: this.id,
            type: 'connection',
            dataset: 'preview',
            title: this.title,
            idLs: Array.from(s),
        };
    }

    get size() {
        return this._xitemIdSet.size || this.previewLs.length;
    }

    get(tag: CTG | 'date', isStatic = true): any {
        switch (tag) {
            case 'date':
                return this.date;
        }

        if (isStatic && tag == 'hits') return `${this._xitemIdSet.size}`;
        return this._valueMap.get(tag);
    }

    set(tag: CTG, val: any) {
        this._valueMap.set(tag, val);
        this.markForManifestStore();
    }

    get title() {
        return this.get('title');
    }

    get description() {
        return this.get('description') || '';
    }

    get timestamp() {
        return this.get('_ts') || 0;
    }

    get date() {
        try {
            const d = Temporal.Instant.fromEpochMilliseconds(this.get('_ts'));
            return this.coords.dateString(d);
        } catch (e) {
            return '';
        }
    }

    delete() {
        this.databin!.deleteConnection(this);
        this.manifestDelete();
        this.coords.display$.markForCheck();
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => this.delete(),
        };
    }

    save() {
        return {
            id: this.id,
            connector: this.connector && this.connector.tag,
            isOn: this.isOn,
            xitemIdLs: this.xitemIdLs,
            values: Object.fromEntries(this._valueMap),
        };
    }

    private _finalizeCommon() {
        this.markForManifestStore();
        this.databin!.markForManifestStore();
        this.databin!.refresh();
        this.activeFetchNumber = 0;
    }

    async finalize(isFinal = true, n: number = 10) {
        if (this.previewLs.length >= n) isFinal = true;
        let readLs: any[];
        if (n < 10) {
            readLs = this.previewLs.slice(0, n);
        } else readLs = this.previewLs;
        const diff = readLs.length - this.previewLs.length;
        if (diff < 0) {
            const sP = this.get('_searchParams');
            const startIndex = sP?.startIndex;
            if (startIndex) sP.startIndex += diff;
        }
        await this.readXItemList(readLs);
        this.previewLs = [];
        this.intoProject();
        isFinal && this._finalizeCommon();
    }

    get dialogTitle() {
        return this.title;
    }

    get xitemIdLs() {
        return Array.from(this._xitemIdSet);
    }

    get idSet() {
        return new Set(this._xitemIdSet);
    }

    async readXItemList(ls: XItem[]) {
        ls.forEach((xitm: any) => {
            if (this._xitemIdSet.has(xitm.id)) return;
            this._xitemIdSet.add(xitm.id);
            this.databin!.addXItem(xitm);
        });
    }

    xitemLsExtend(ls: XItem[]) {
        this.databin!.updateXitemMap(ls);
        ls.forEach((xitm) => this._xitemIdSet.add(xitm.id));
        this.xitemLsChange$.next(true);
        this.markForManifestStore();
    }

    public get isOn() {
        return this._valueMap.get('isOn')! as boolean;
    }

    public set isOn(value: boolean) {
        this._valueMap.set('isOn', value);
        this.markForManifestStore();
        this.databin?.refresh();
    }

    importLink(href: string) {
        const url = new URL(href);
        if (url.protocol !== 'https:') {
            console.error('Only https links are supported', url.toString());
            return;
        }

        fetch(url)
            .then((res) => res.text())
            .then((text) => {
                console.log(text);
            });
    }

    initDialog() {
        const ec = this.coords.databin$.editedConnection$.getValue();
        if (!ec || ec !== this) this.coords.databin$.editedConnection$.next(this);
        this.coords.databin$.communicatorStage$.next(1);
    }

    editDialog() {
        this.coords.databin$.editedConnection$.next(this);
        this.coords.databin$.communicatorStage$.next(4);
    }

    deleteDialog() {
        this.coords.display$.delete$.next(this);
    }

    get deleteInfo() {
        return `
        <div>
        <div class='delete_info_sub'>
        <h2>${this.coords.text('Type')}:</h2>
        <span>
        ${this.coords.text('Resource')}
        </span>
        </div>
        <div class='delete_info_sub'>
        <h2>${this.coords.text('Title')}:</h2>
        <span>
        ${this.title}
        </span>
        </div>
        <div class='delete_info_sub'>
        <h2>${this.coords.text('Size')}:</h2>
        <span>
        ${this.size}
        </span>
        </div>
        </div>
        `;
    }

    /**
     * If it's online resource, we can fetch additional references
     * up to 12 hours after the connection was created.
     */
    get canFetchAdditionalReferences() {
        if (!this.connector.isOnline) return true;

        try {
            const createdAt = this.get('_ts');
            const timePassedMs = new Date().valueOf() - createdAt;

            return timePassedMs < this._staleConnectionThresholdMs;
        } catch (e) {
            console.error("Can't check if additional references can be fetched: ", e);
            return false;
        }
    }

    get isFresh() {
        return true;
    }

    get isFileImport() {
        return this.connector.hasFileImport;
    }

    get inProject() {
        return this.databin!.hasConnection(this);
    }

    intoProject() {
        this.databin?.addConnection(this);
    }

    preview() {
        this.coords.resultList$.activateSubset({
            idSet: this._xitemIdSet,
            title: this.title,
            prefix: this.coords.text('connection'),
            icon: 'resource',
            class: 'selection-in-general',
            back: () => this.coords.stage$.set('databin'),
            stage: 'preview',
        });
    }

    get communicator() {
        const tag = this.connector.isOnline ? 'online' : this.connector.tag;
        return this.coords.databin$.communicatorMap.get(tag);
    }

    get xitemLs() {
        return Array.from(this._xitemIdSet)
            .map((id) => this.databin?.xitemMap.get(id))
            .filter((x) => x) as XItem[];
    }

    get helpHtml() {
        const ls: string[] = [];
        ls.push(`<b>${this.coords.text('Size-includingDuplicates')}: </b>${this.size}`);

        for (const x of this.xitemIdLs) {
            const dt = this.coords.dataset$.preview;
            const xitm = x && dt.get(x);
            xitm && ls.push(xitm.title);
            if (ls.length >= 7) {
                ls.push('...');
                break;
            }
        }
        return ls.map((x) => `<div class='one-line'>${x}</div>`).join('\n');
    }

    get scriptClass() {
        return new ScriptResource(this.coords, this);
    }
}
